import React, { useEffect, useState } from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CloseIcon from "@mui/icons-material/Close";
import { Link as RouterLink } from "react-router-dom";
import usageService from "../services/usageService";
import AuthService from "../services/authService";

const CONTACT_EMAIL = "contact@voras.com";

// Aviso proactivo del cupo de egresos del mes. Aparece al acercarse (warning, >=90%) o al
// alcanzar el tope (blocked, >=100%). En "blocked" no se puede descartar; en "warning" sí.
// Espeja el patrón de PlanBanner. La acción "Mejorar plan" hoy es un contacto (mailto);
// cuando entre Stripe se reemplaza por el Checkout/Customer Portal.
const EgressUsageBanner = () => {
  const [usage, setUsage] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let active = true;
    const userId = Number(sessionStorage.getItem("User"));
    if (!userId) return undefined;

    usageService
      .getEgresoUsage(userId)
      .then((res) => {
        if (active) setUsage(res);
      })
      .catch(() => {
        // Sin datos de uso (p. ej. usuario no-owner o endpoint no disponible): no se muestra.
      });

    return () => {
      active = false;
    };
  }, []);

  if (!usage || dismissed) return null;

  const { status, plan, used, limit } = usage;
  if (status !== "warning" && status !== "blocked") return null;

  const blocked = status === "blocked";
  const palette = blocked
    ? { bg: "#fef2f2", border: "#fecaca", fg: "#b91c1c", icon: "#dc2626" }
    : { bg: "#fffbeb", border: "#fde68a", fg: "#92400e", icon: "#d97706" };

  const planTxt = plan ? ` ${plan}` : "";
  const mensaje = blocked
    ? `Alcanzaste el límite de egresos de tu plan${planTxt} (${used}/${limit} este mes). Tus ventas se siguen recibiendo, pero no podés prepararlas ni crear egresos nuevos hasta mejorar el plan.`
    : `Estás cerca del límite de egresos de tu plan${planTxt} (${used}/${limit} este mes). Mejorá el plan para no interrumpir tus despachos.`;

  const mailto =
    `mailto:${CONTACT_EMAIL}` +
    `?subject=${encodeURIComponent("Mejorar plan Voras")}` +
    `&body=${encodeURIComponent("Hola, quiero mejorar mi plan de Voras.")}`;

  // /plans es owner-only: el owner va a la sección de planes; el colaborador, que no
  // puede gestionar el plan, mantiene el contacto.
  const ctaProps = AuthService.isOwner()
    ? { component: RouterLink, to: "/plans" }
    : { href: mailto };

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 20,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: { xs: 2, md: 3 },
        py: 1.25,
        backgroundColor: palette.bg,
        borderBottom: `1px solid ${palette.border}`,
      }}
    >
      <WarningAmberRoundedIcon sx={{ color: palette.icon, fontSize: 22, flexShrink: 0 }} />
      <Typography sx={{ flexGrow: 1, fontSize: "0.85rem", fontWeight: 500, color: palette.fg }}>
        {mensaje}
      </Typography>
      <Button
        {...ctaProps}
        variant="contained"
        disableElevation
        sx={{
          flexShrink: 0,
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.8rem",
          borderRadius: "8px",
          px: 2,
          backgroundColor: palette.fg,
          "&:hover": { backgroundColor: palette.icon },
        }}
      >
        Mejorar plan
      </Button>
      {/* El aviso "blocked" permanece; el de "warning" se puede cerrar. */}
      {!blocked && (
        <IconButton
          size="small"
          onClick={() => setDismissed(true)}
          aria-label="Cerrar aviso"
          sx={{ flexShrink: 0, color: palette.fg }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}
    </Box>
  );
};

export default EgressUsageBanner;
